import type { BudgetFinancials } from '../services/budget-api';

/**
 * The Budget's lines and every figure derived from them -- one place, no React. A budget is one row per project whose `items` hold
 * the MANUAL lines (the user's own estimates). Budget = Qty x Unit Cost.
 *
 * For every cost code the table shows two kinds of line, kept apart and grouped under the code:
 *   - MANUAL lines: stored in the budget, edited and deleted by the user, never touched by purchase orders. Budget is theirs; Committed and Billed are 0.
 *   - ONE consolidated "POs / Bills / Change Orders / Selections" line per cost code: NOT stored -- it is computed from the purchase orders, bills and change orders each time (so it can never be
 *     duplicated by reloading), cannot be edited or deleted here, and always traces back to its documents. Budget = the change orders' items on the code
 *     (every state but rejected / cancelled); Committed = the TOTAL value of the
 *     project's valid purchase orders on the code; Billed = what the bills of those orders billed.
 *   - ONE "Bills (no PO)" line per cost code that has bills with no purchase order: Budget 0, Committed 0, Billed = those bills.
 *
 * Every row can carry a CLIENT PRICE: Client Pricing = cost basis x (1 + Markup % / 100). The cost basis of a manual line is its Budget, of the
 * "POs / Bills" line its Committed, of the "Bills (no PO)" line its Billed. The user edits EITHER Markup or Client Pricing; the last one typed is the
 * SOURCE (it alone is stored: `RowPricing`) and the other is derived, so nothing drifts and a Client Pricing typed by hand stays put when the cost changes.
 * Parent rows and totals only add up the rows' Client Pricing (once); they are never edited.
 *
 * BUDGET (the cost of the job, never marked up) = the manual lines + the change orders' builder cost (every state but rejected / cancelled) + Budget Tax (% of the
 * manual lines) + Budget Other Amount. CLIENT PRICE = the Client Pricing of the rows + Client Pricing Tax (% of that) + Client Pricing Other Amount. The two
 * columns of the bottom table have their own Tax and Other fields. The markup only ever touches Client Pricing. (Selections have no approved state yet, so they
 * add nothing; change orders' client cost is not in Client Price until their rules are defined.)
 *
 * COMMITTED = what the valid orders still have open + everything billed (the server's `commitment`): bills with no order, bills above their order and bills of a
 * rejected / cancelled order are commitments; a bill an order already covers is not counted twice. The "POs / Bills" row carries the part that comes from orders,
 * the "Bills (no PO)" row the part billed with no order.
 *
 * Committed and Billed are different measures, not categories: a $150 order with a $100 bill is Committed 150 and Billed 100 (billing never reduces
 * Committed; payments change neither). Each column is therefore added up on its own -- per cost code and for the project.
 *   Remaining = Budget - Billed
 *   Available to Commit = Budget - Committed
 */
/** What the user typed on a row: the Markup (%) or the Client Pricing -- whichever was typed last. The other one is derived from it. */
export type RowPricing = { source: 'markup'; markupPct: number } | { source: 'client'; clientPrice: number };

/** The calculated Markup / Client Pricing of a row. `markupPct` is null when there is no cost to apply a percentage to (never NaN / Infinity). */
export interface RowPrice { markupPct: number | null; clientPrice: number }

/** The pricing of the calculated rows ("orders-<cost code>" / "bills-<cost code>"), stored in budgets.pricing_overrides. */
export type PricingOverrides = Record<string, RowPricing>;

export interface BudgetLine {
  id: string;
  /** the cost code (Catalog) by id; '' = none */
  costCodeId: string;
  /** the code's text when the line was last saved: only what is shown if the code can no longer be found */
  costCode: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost: number;
  /** Markup / Client Pricing typed by the user; absent = no markup (Client Pricing = Budget) */
  pricing?: RowPricing;
}

/** A line as it is stored in budgets.items. `total` and `revisedClientPrice` carry the line's budget so every reader of the document total keeps working. */
export interface StoredBudgetItem extends BudgetLine {
  total: number;
  revisedClientPrice: number;
}

export type BudgetRowKind = 'manual' | 'orders' | 'bills';

/** One document behind a consolidated line (an order with what it commits, or a bill with what it billed). */
export interface BudgetSourceDocument {
  kind: 'order' | 'bill' | 'change_order' | 'selection' | 'material_order';
  id: string;
  label: string;
  /** an order's value, a bill's billed amount or a change order's builder cost (its Budget) on this cost code */
  amount: number;
  /** change orders and selections only: its Client Price, when it sits on this cost code (the default one) */
  clientAmount?: number;
}

export interface BudgetRow {
  /** manual: the line's id; consolidated: `orders-<cost code>` / `bills-<cost code>` */
  id: string;
  kind: BudgetRowKind;
  costCodeId: string;
  /** manual rows only */
  line?: BudgetLine;
  budget: number;
  committed: number;
  billed: number;
  /** Budget - Billed */
  remaining: number;
  /** the amount the Markup applies to: Budget (manual), Committed (POs / Bills) or Billed (Bills with no PO) */
  basis: number;
  markupPct: number | null;
  clientPrice: number;
  /** consolidated rows: the orders and bills behind the figures */
  documents?: BudgetSourceDocument[];
  /** the "POs / Bills / Change Orders / Selections" row of the default cost code: the Client Price of the change orders and selections, part of `clientPrice` but not of what the markup works on */
  linkedClient?: number;
}

export interface BudgetFigures { budget: number; committed: number; billed: number; remaining: number; basis: number; clientPrice: number }

/** All the rows of one cost code (its manual lines first, then the consolidated ones) and their sum, column by column. */
export interface BudgetCodeBlock {
  costCodeId: string;
  rows: BudgetRow[];
  /** shown when the code has more than one row */
  total: BudgetFigures;
}

/** A Cost Code Category (the parent level); `label` is null for the items that have no category. `total` sums its items' blocks once. */
export interface BudgetSection {
  key: string;
  label: string | null;
  blocks: BudgetCodeBlock[];
  total: BudgetFigures;
}

export interface BudgetTotals extends BudgetFigures {
  /** Budget - Committed */
  available: number;
  /** the manual lines alone (the Totals row) */
  subtotal: number;
  /** the change orders' builder cost (every state but rejected / cancelled) */
  changeOrders: number;
  /** the SELECTED options' items of the selections (builder cost) */
  selections: number;
  /** the material orders no purchase order stands for yet (their total; once a purchase order stands, Committed carries it instead) */
  materialOrders: number;
  /** Budget Tax in money (% of the manual lines) and Budget Other Amount: `budget` = subtotal + changeOrders + selections + materialOrders + tax + other */
  tax: number;
  other: number;
  /** the Client Pricing of every row, before the Client Pricing charges */
  clientSubtotal: number;
  /** Client Pricing Tax in money (% of clientSubtotal) and Client Pricing Other Amount: `clientPrice` = clientSubtotal + clientTax + clientOther */
  clientTax: number;
  clientOther: number;
}

/** The two columns' own Tax (%) and Other Amount: Budget's and Client Pricing's, independent. (Not the POs' / bills' charges.) */
export interface BudgetCharges { taxPct: number; otherAmount: number; clientTaxPct: number; clientOtherAmount: number }
const NO_CHARGES: BudgetCharges = { taxPct: 0, otherAmount: 0, clientTaxPct: 0, clientOtherAmount: 0 };

export interface BudgetView {
  sections: BudgetSection[];
  /** manual lines with no cost code yet (a new line starts here) */
  noCodeRows: BudgetRow[];
  /** money that belongs to no cost code at all (`budget`: change orders no item explains, such as one from before items existed) */
  noCode: { budget: number; committed: number; billed: number; clientPrice: number };
  totals: BudgetTotals;
}

/** The Budget's status values (budgets.status) -- the labels and colours are GenericTag's, the same on the Budget page and in the Overview. */
export const BUDGET_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'in_revision', label: 'In Revision' },
  { value: 'approved', label: 'Approved' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;
export type BudgetStatus = typeof BUDGET_STATUSES[number]['value'];

/** A stored status, or 'draft' for a missing / unknown one. */
export const budgetStatusOf = (value: unknown): BudgetStatus => (BUDGET_STATUSES.find((s) => s.value === value)?.value ?? 'draft');

export const DEFAULT_UNIT = 'ea';

export const roundCents = (value: number): number => Math.round((Number(value) || 0) * 100) / 100;

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const lineBudget = (line: Pick<BudgetLine, 'quantity' | 'unitCost'>): number => roundCents(num(line.quantity) * num(line.unitCost));

export function newLine(overrides: Partial<BudgetLine> = {}): BudgetLine {
  const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return { id, costCodeId: '', costCode: '', description: '', quantity: 1, unit: DEFAULT_UNIT, unitCost: 0, ...overrides };
}

/** A stored pricing, or undefined when it is missing / not a usable number (the row then has no markup). */
export function toRowPricing(value: any): RowPricing | undefined {
  if (!value || typeof value !== 'object') return undefined;
  if (value.source === 'markup' && Number.isFinite(Number(value.markupPct))) return { source: 'markup', markupPct: Number(value.markupPct) };
  if (value.source === 'client' && Number.isFinite(Number(value.clientPrice))) return { source: 'client', clientPrice: Number(value.clientPrice) };
  return undefined;
}

/** The stored overrides of the calculated rows, keeping only the usable ones. */
export function toPricingOverrides(value: any): PricingOverrides {
  const result: PricingOverrides = {};
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    Object.keys(value).forEach((key) => { const pricing = toRowPricing(value[key]); if (pricing) result[key] = pricing; });
  }
  return result;
}

const round4 = (value: number): number => Math.round(value * 10000) / 10000;

/**
 * Markup and Client Pricing of a row from its cost basis and what the user typed. Only the typed value (the source) is stored; the other is derived
 * here, so the pair never drifts. Without a positive cost there is no percentage to show (markupPct = null), never NaN / Infinity.
 */
export function priceRow(basis: number, pricing?: RowPricing): RowPrice {
  const cost = Math.max(roundCents(basis), 0);
  if (pricing && pricing.source === 'client') {
    const clientPrice = roundCents(pricing.clientPrice);
    return { clientPrice, markupPct: cost > 0 ? round4((clientPrice / cost - 1) * 100) : null };
  }
  const markupPct = pricing ? pricing.markupPct : 0;
  return { markupPct, clientPrice: roundCents(cost * (1 + markupPct / 100)) };
}

/**
 * priceRow for the "POs / Bills / Change Orders / Selections / Material Orders" row. Purchase orders, bills and material orders have NO Client Price: nothing is
 * priced from Committed. Only the change orders' and selections' own Client Price (`linkedClient`) is added, as it is.
 */
const priceRowWithLinked = (basis: number, pricing: RowPricing | undefined, linkedClient: number): RowPrice => {
  const priced = priceRow(basis, pricing);
  return { markupPct: priced.markupPct, clientPrice: roundCents(priced.clientPrice + linkedClient) };
};

/** The markup a set of rows works out to as a whole (their total Client Pricing over their total cost); null without cost. Display only. */
export const overallMarkup = (basis: number, clientPrice: number): number | null => (basis > 0 ? round4((clientPrice / basis - 1) * 100) : null);

/**
 * A stored item as a line. Lines written by this table carry `unitCost`; lines written by the older budget form / Bob carry
 * `unitPrice` and a `revisedClientPrice` that IS the line's amount -- those keep their amount (unit cost = amount / qty).
 * (An `imported` flag that an earlier version stored is simply dropped: such a line is the user's own estimate and stays a manual line.)
 */
export function toBudgetLine(item: any): BudgetLine {
  const quantity = item && item.quantity !== undefined && item.quantity !== null && item.quantity !== '' ? num(item.quantity) : 1;
  let unitCost: number;
  if (item && item.unitCost !== undefined && item.unitCost !== null) unitCost = num(item.unitCost);
  else if (item && item.revisedClientPrice !== undefined && quantity > 0) unitCost = roundCents(num(item.revisedClientPrice) / quantity);
  else unitCost = num(item && item.unitPrice);
  return {
    id: String((item && item.id) || newLine().id),
    costCodeId: String((item && item.costCodeId) || ''),
    costCode: String((item && item.costCode) || ''),
    description: String((item && item.description) || ''),
    quantity,
    unit: String((item && item.unit) || DEFAULT_UNIT),
    unitCost,
    ...(toRowPricing(item && item.pricing) ? { pricing: toRowPricing(item.pricing) as RowPricing } : {}),
  };
}

export function toStoredItem(line: BudgetLine): StoredBudgetItem {
  const total = lineBudget(line);
  return { ...line, total, revisedClientPrice: total };
}

/**
 * A line nobody filled in is not saved (a blank row is a place to type, not a budget line). A new line starts with the company's default cost code, so
 * having ONLY that code (`defaultCostCodeId`) does not make a line worth saving.
 */
export const isBlankLine = (line: BudgetLine, defaultCostCodeId = ''): boolean =>
  !line.description.trim() && (!line.costCodeId || line.costCodeId === defaultCostCodeId) && num(line.unitCost) === 0;

export function budgetTotal(lines: BudgetLine[]): number {
  return roundCents(lines.reduce((sum, line) => sum + lineBudget(line), 0));
}

/** A cost code of the company's catalog; `parent_id` is its Parent cost code (null = it is itself a Parent: a group, never a budget line). */
export interface CatalogCode { id: string; code: string; name: string | null; parent_id?: string | null; default_key?: string | null }

/** "1000 — Drywall" for a parent cost code. */
const parentLabel = (parent: CatalogCode): string => (parent.name ? `${parent.code} — ${parent.name}` : parent.code);

const sumFigures = (rows: BudgetRow[]): BudgetFigures => {
  const budget = roundCents(rows.reduce((s, r) => s + r.budget, 0));
  const committed = roundCents(rows.reduce((s, r) => s + r.committed, 0));
  const billed = roundCents(rows.reduce((s, r) => s + r.billed, 0));
  const basis = roundCents(rows.reduce((s, r) => s + r.basis, 0));
  const clientPrice = roundCents(rows.reduce((s, r) => s + r.clientPrice, 0));
  return { budget, committed, billed, remaining: roundCents(budget - billed), basis, clientPrice };
};

const codeCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/**
 * Everything the table shows, from the stored manual lines and what the server says per cost code. Nothing here is stored or sent back: the consolidated
 * rows exist only as the sum of their documents, so they cannot be duplicated, edited or lost.
 * Order: parent cost codes (by code; cost codes with no parent last), cost codes inside a group by code, and inside a
 * code the manual lines in the order they were created, then the consolidated ones. Manual lines with no cost code yet come last, in creation order.
 */
export function buildBudgetView(lines: BudgetLine[], financials: BudgetFinancials | null, codes: CatalogCode[] = [], overrides: PricingOverrides = {}, charges: BudgetCharges = NO_CHARGES): BudgetView {
  const byCode = financials?.by_cost_code ?? {};
  const sources = new Set(financials?.source_codes ?? []);
  const documents = financials?.documents;
  const codeById = new Map(codes.map((c) => [c.id, c]));

  const manualRow = (line: BudgetLine): BudgetRow => {
    const budget = lineBudget(line);
    return { id: line.id, kind: 'manual', costCodeId: line.costCodeId, line, budget, committed: 0, billed: 0, remaining: budget, basis: budget, ...priceRow(budget, line.pricing) };
  };

  const documentsOf = (id: string, kind: 'orders' | 'bills'): BudgetSourceDocument[] => {
    const entry = byCode[id];
    if (!entry) return [];
    const result: BudgetSourceDocument[] = [];
    if (kind === 'orders') {
      for (const po of entry.purchase_orders ?? []) result.push({ kind: 'order', id: po.id, label: documents?.purchase_orders?.[po.id]?.label ?? 'Purchase order', amount: po.committed });
    }
    for (const bill of entry.bills ?? []) {
      if ((kind === 'orders') === bill.linked) result.push({ kind: 'bill', id: bill.id, label: documents?.bills?.[bill.id]?.label ?? 'Bill', amount: bill.billed });
    }
    return result;
  };

  // the cost codes of the table: those the user put a line on, and every one the server has money or purchase orders on
  // Documents that put money in the Budget without being purchase orders or bills: CHANGE ORDERS and SELECTIONS (the selected options' items). Their builder
  // cost sits on the cost codes of their items; their Client Price (one figure each) all on the default cost code, once. They are never Committed.
  const linkedSources = [
    { kind: 'change_order' as const, block: financials?.change_orders, names: documents?.change_orders, fallback: 'Change order' },
    { kind: 'selection' as const, block: financials?.selections, names: documents?.selections, fallback: 'Selection' },
    // material orders with no purchase order standing: their cost sits on their items' cost codes, in the Budget column (no Client Price of their own)
    { kind: 'material_order' as const, block: financials?.material_orders, names: documents?.material_orders, fallback: 'Material order' },
  ];
  const coByCode: Record<string, number> = {};
  for (const source of linkedSources) for (const [id, value] of Object.entries(source.block?.by_cost_code ?? {})) coByCode[id] = roundCents((coByCode[id] ?? 0) + num(value));
  const coClientCode = linkedSources.map((source) => source.block?.client_cost_code).find((code) => !!code) ?? '';
  const coClientTotal = roundCents(linkedSources.reduce((sum, source) => sum + (source.block?.client_documents ?? []).reduce((inner, d) => inner + num(d.client), 0), 0));
  const linkedDocuments = (codeId: string): BudgetSourceDocument[] => {
    const result: BudgetSourceDocument[] = [];
    for (const source of linkedSources) {
      const byId = new Map<string, BudgetSourceDocument>();
      const nameOf = (id: string) => source.names?.[id]?.label ?? source.fallback;
      for (const d of source.block?.documents?.[codeId] ?? []) byId.set(d.id, { kind: source.kind, id: d.id, label: nameOf(d.id), amount: roundCents(num(d.builder)) });
      if (codeId === coClientCode) {
        for (const d of source.block?.client_documents ?? []) {
          const found = byId.get(d.id);
          if (found) found.clientAmount = roundCents(num(d.client));
          else byId.set(d.id, { kind: source.kind, id: d.id, label: nameOf(d.id), amount: 0, clientAmount: roundCents(num(d.client)) });
        }
      }
      result.push(...Array.from(byId.values()));
    }
    return result;
  };
  const ids = new Set<string>();
  for (const line of lines) if (line.costCodeId) ids.add(line.costCodeId);
  for (const id of Object.keys(byCode)) ids.add(id);
  for (const id of Object.keys(coByCode)) ids.add(id);
  if (coClientCode && coClientTotal !== 0) ids.add(coClientCode);
  sources.forEach((id) => ids.add(id));

  const blocks = new Map<string, BudgetCodeBlock>();
  ids.forEach((id) => {
    const entry = byCode[id];
    const rows: BudgetRow[] = lines.filter((l) => l.costCodeId === id).map(manualRow);
    const linkedBudget = roundCents(num(coByCode[id]));
    const linkedClient = id === coClientCode ? coClientTotal : 0;
    if ((entry && (num(entry.commitment_orders) !== 0 || num(entry.billed_po) !== 0)) || sources.has(id) || linkedBudget !== 0 || linkedClient !== 0) {
      // Budget of this row = the change orders' items on this code (never Committed: a change order commits nothing until a purchase order is made from it).
      // Committed of this row = what its orders still have open + what was billed against them (== the orders' value, unless a bill went above / its order is no longer valid)
      const committed = roundCents(num(entry?.commitment_orders));
      rows.push({ id: `orders-${id}`, kind: 'orders', costCodeId: id, budget: linkedBudget, committed, billed: roundCents(num(entry?.billed_po)), remaining: roundCents(linkedBudget - num(entry?.billed_po)), basis: 0, ...priceRowWithLinked(0, undefined, linkedClient), linkedClient, documents: [...documentsOf(id, 'orders'), ...linkedDocuments(id)] });
    }
    if (entry && num(entry.billed_other) !== 0) {
      const billed = roundCents(num(entry.billed_other));
      // bills with no order are commitments of their own
      rows.push({ id: `bills-${id}`, kind: 'bills', costCodeId: id, budget: 0, committed: billed, billed, remaining: roundCents(-num(entry.billed_other)), basis: 0, ...priceRow(0, undefined), documents: documentsOf(id, 'bills') });
    }
    blocks.set(id, { costCodeId: id, rows, total: sumFigures(rows) });
  });

  // sections: one per PARENT cost code (a child's parent); cost codes with no parent -- legacy codes used before the hierarchy -- go last
  const sectionKey = (id: string) => {
    const pid = codeById.get(id)?.parent_id;
    return pid && codeById.has(pid) ? pid : '';
  };
  // the Unassigned parent is the parent of the company's default cost code (identified by that link, not by its number or name)
  const unassignedKey = codes.find((c) => c.default_key)?.parent_id ?? '';
  const sections = new Map<string, BudgetSection>();
  blocks.forEach((block) => {
    const key = sectionKey(block.costCodeId);
    if (!sections.has(key)) sections.set(key, { key, label: key ? parentLabel(codeById.get(key) as CatalogCode) : null, blocks: [], total: sumFigures([]) });
    sections.get(key)?.blocks.push(block);
  });
  const textOf = (id: string) => codeById.get(id)?.code ?? lines.find((l) => l.costCodeId === id)?.costCode ?? id;
  const ordered = Array.from(sections.values())
    .map((section) => {
      const sorted = [...section.blocks].sort((a, b) => codeCompare(textOf(a.costCodeId), textOf(b.costCodeId)));
      // each item's rows belong to exactly one block, so summing the blocks counts every row once
      return { ...section, blocks: sorted, total: sumFigures(sorted.flatMap((b) => b.rows)) };
    })
    .sort((a, b) => {
      // Parent categories by code; cost codes with no parent next to last; the company's Unassigned parent (whatever its number) always last
      const rank = (key: string) => (key === unassignedKey ? 2 : key ? 0 : 1);
      return rank(a.key) - rank(b.key) || codeCompare(codeById.get(a.key)?.code ?? '', codeById.get(b.key)?.code ?? '');
    });

  const noCodeRows = lines.filter((l) => !l.costCodeId).map(manualRow);

  // BUDGET = the manual lines + the change orders' builder cost + Budget Tax (% of the manual lines) + Budget Other Amount, in whole cents.
  // The markup never enters it. Remaining and Available use this one figure.
  const subtotal = budgetTotal(lines);
  const changeOrders = roundCents(num(financials?.change_orders?.builder_cost));
  const selections = roundCents(num(financials?.selections?.builder_cost));
  const materialOrders = roundCents(num(financials?.material_orders?.builder_cost));
  const tax = roundCents(subtotal * (Math.max(num(charges.taxPct), 0) / 100));
  const other = roundCents(Math.max(num(charges.otherAmount), 0));
  const budget = roundCents(subtotal + changeOrders + selections + materialOrders + tax + other);
  const totalCommitted = financials ? num(financials.totals.commitment) : 0;
  const totalBilled = financials ? financials.totals.actual : 0;
  // Money that belongs to no cost code has no row to price: it counts at cost (no markup) in Client Pricing, once
  const noCodeCommitted = roundCents(num(financials?.unassigned?.commitment));
  const allRows = [...ordered.flatMap((section) => section.blocks.flatMap((b) => b.rows)), ...noCodeRows];
  const rowTotals = sumFigures(allRows);
  // CLIENT PRICE = the Client Pricing of every row (manual lines and POs / Bills rows are all added, none is deduplicated) + its own Tax (% of that) + Other Amount
  const noCodeClient = coClientCode ? 0 : coClientTotal; // a company with no default cost code has nowhere to put it: at the end, counted once
  const clientSubtotal = roundCents(rowTotals.clientPrice + noCodeClient);
  const clientTax = roundCents(clientSubtotal * (Math.max(num(charges.clientTaxPct), 0) / 100));
  const clientOther = roundCents(Math.max(num(charges.clientOtherAmount), 0));
  return {
    sections: ordered,
    noCodeRows,
    noCode: {
      budget: roundCents(num(financials?.change_orders?.unassigned) + num(financials?.selections?.unassigned) + num(financials?.material_orders?.unassigned)),
      clientPrice: noCodeClient,
      committed: noCodeCommitted,
      billed: roundCents(financials?.unassigned?.actual ?? 0),
    },
    totals: {
      subtotal,
      changeOrders,
      selections,
      materialOrders,
      tax,
      other,
      budget,
      committed: roundCents(totalCommitted),
      billed: roundCents(totalBilled),
      remaining: roundCents(budget - totalBilled),
      available: roundCents(budget - totalCommitted),
      basis: roundCents(rowTotals.basis),
      clientSubtotal,
      clientTax,
      clientOther,
      clientPrice: roundCents(clientSubtotal + clientTax + clientOther),
    },
  };
}
